import { useState, useEffect } from 'react'
import { TrendingUp, Activity, RefreshCw, ExternalLink, ChevronRight, BarChart3, AlertCircle } from 'lucide-react'

interface PolymarketMarket {
  id: string
  question: string
  slug: string
  volume: string
  volumeNum: number
  liquidity: string
  liquidityNum: number
  outcomePrices: string
  outcomes: string
  description: string
  image: string | null
  endDate: string | null
  active: boolean
  closed: boolean
  category?: string
}

// 备选演示数据
const DEMO_MARKETS: PolymarketMarket[] = [
  {
    id: "680392",
    question: "US government shutdown Saturday?",
    slug: "will-there-be-another-us-government-shutdown-by-january-31",
    volume: "140460182",
    volumeNum: 140460182,
    liquidity: "5646974",
    liquidityNum: 5646974,
    outcomePrices: "[\"0.9995\", \"0.0005\"]",
    outcomes: "[\"Yes\", \"No\"]",
    description: "Will there be a US government shutdown?",
    image: "https://polymarket-upload.s3.us-east-2.amazonaws.com/govt+shutdown.jpeg",
    endDate: "2026-01-31T00:00:00Z",
    active: true,
    closed: false,
    category: "Politics"
  },
  {
    id: "572473",
    question: "Will Trump nominate Judy Shelton as the next Fed chair?",
    slug: "will-trump-nominate-judy-shelton-as-the-next-fed-chair",
    volume: "45947874",
    volumeNum: 45947874,
    liquidity: "1317487",
    liquidityNum: 1317487,
    outcomePrices: "[\"0.0125\", \"0.9875\"]",
    outcomes: "[\"Yes\", \"No\"]",
    description: "Trump Fed nomination",
    image: "https://polymarket-upload.s3.us-east-2.amazonaws.com/judy-shelton-15c5dd306b.png",
    endDate: "2026-12-31T00:00:00Z",
    active: true,
    closed: false,
    category: "Politics"
  },
  {
    id: "566203",
    question: "Will Leeds win the 2025–26 English Premier League?",
    slug: "will-leeds-win-the-202526-english-premier-league",
    volume: "35607235",
    volumeNum: 35607235,
    liquidity: "1033594",
    liquidityNum: 1033594,
    outcomePrices: "[\"0.0015\", \"0.9985\"]",
    outcomes: "[\"Yes\", \"No\"]",
    description: "Premier League winner",
    image: "https://polymarket-upload.s3.us-east-2.amazonaws.com/will-leeds-win-the-202526-english-premier-league-tSaX7zrXzuyD.jpg",
    endDate: "2026-05-27T00:00:00Z",
    active: true,
    closed: false,
    category: "Sports"
  },
  {
    id: "559687",
    question: "Will Oprah Winfrey win the 2028 Democratic presidential nomination?",
    slug: "will-oprah-winfrey-win-the-2028-democratic-presidential-nomination",
    volume: "32206460",
    volumeNum: 32206460,
    liquidity: "490680",
    liquidityNum: 490680,
    outcomePrices: "[\"0.0085\", \"0.9915\"]",
    outcomes: "[\"Yes\", \"No\"]",
    description: "Democratic nomination",
    image: "https://polymarket-upload.s3.us-east-2.amazonaws.com/oprah+winfrey.png",
    endDate: "2028-11-07T00:00:00Z",
    active: true,
    closed: false,
    category: "Politics"
  },
  {
    id: "559684",
    question: "Will Chelsea Clinton win the 2028 Democratic presidential nomination?",
    slug: "will-chelsea-clinton-win-the-2028-democratic-presidential-nomination",
    volume: "31340641",
    volumeNum: 31340641,
    liquidity: "662009",
    liquidityNum: 662009,
    outcomePrices: "[\"0.0075\", \"0.9925\"]",
    outcomes: "[\"Yes\", \"No\"]",
    description: "Democratic nomination",
    image: "https://polymarket-upload.s3.us-east-2.amazonaws.com/chelsea+clinton.png",
    endDate: "2028-11-07T00:00:00Z",
    active: true,
    closed: false,
    category: "Politics"
  }
]

const parseOutcomePrices = (pricesJson: string): { yes: number; no: number } => {
  try {
    const prices = JSON.parse(pricesJson)
    return {
      yes: Math.round((parseFloat(prices[0]) || 0) * 100),
      no: Math.round((parseFloat(prices[1]) || 0) * 100)
    }
  } catch {
    return { yes: 50, no: 50 }
  }
}

const formatVolume = (volume: number): string => {
  if (volume >= 1000000) return `$${(volume / 1000000).toFixed(2)}M`
  if (volume >= 1000) return `$${(volume / 1000).toFixed(1)}K`
  return `$${volume.toFixed(0)}`
}

const formatEndDate = (dateString: string | null): string => {
  if (!dateString) return '未设定'
  const date = new Date(dateString)
  const now = new Date()
  const diff = date.getTime() - now.getTime()
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24))
  if (days < 0) return '已结束'
  if (days === 0) return '今天结束'
  if (days === 1) return '明天结束'
  if (days < 7) return `${days}天后结束`
  return `${Math.floor(days / 7)}周后结束`
}

const getCategoryColor = (category: string | undefined): string => {
  const colors: { [key: string]: string } = {
    'Politics': 'bg-blue-500/20 text-blue-400',
    'Crypto': 'bg-purple-500/20 text-purple-400',
    'Sports': 'bg-green-500/20 text-green-400',
    'Business': 'bg-yellow-500/20 text-yellow-400',
    'Science': 'bg-cyan-500/20 text-cyan-400',
    'Entertainment': 'bg-pink-500/20 text-pink-400',
  }
  return colors[category || ''] || 'bg-gray-500/20 text-gray-400'
}

const getCategoryName = (category: string | undefined): string => {
  const names: { [key: string]: string } = {
    'Politics': '政治',
    'Crypto': '加密货币',
    'Sports': '体育',
    'Business': '商业',
    'Science': '科学',
    'Entertainment': '娱乐',
    'Technology': '科技',
    'Finance': '金融',
  }
  return names[category || ''] || category || '预测'
}

export default function PolymarketMarkets() {
  const [markets, setMarkets] = useState<PolymarketMarket[]>(DEMO_MARKETS)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [isDemo, setIsDemo] = useState(true)

  const fetchMarkets = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = new URLSearchParams({
        active: 'true',
        closed: 'false',
        limit: '10',
        order: 'volumeNum',
        ascending: 'false',
      })
      
      const response = await fetch(
        `https://gamma-api.polymarket.com/markets?${params}`,
        {
          headers: { 'Accept': 'application/json' },
          signal: AbortSignal.timeout(5000)
        }
      )
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      
      const data = await response.json()
      
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('No data returned')
      }
      
      const processedMarkets = data.map((market: any) => ({
        ...market,
        category: market.events?.[0]?.category || 'Politics'
      })).filter((m: PolymarketMarket) => m.question && m.active && !m.closed)
      
      if (processedMarkets.length > 0) {
        setMarkets(processedMarkets.slice(0, 5))
        setIsDemo(false)
      }
    } catch (err: any) {
      console.error('Polymarket API error:', err)
      setError('API 连接失败，显示演示数据')
      // Keep demo data
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMarkets()
  }, [])

  const categories = [...new Set(markets.map(m => m.category).filter(Boolean))] as string[]
  const filteredMarkets = selectedCategory
    ? markets.filter(m => m.category === selectedCategory)
    : markets

  return (
    <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl border border-indigo-500/30 overflow-hidden">
      <div className="px-4 py-3 border-b border-indigo-500/20 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-indigo-500/20 rounded-lg">
            <BarChart3 className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h3 className="font-semibold text-telegram-text">Polymarket 热门市场</h3>
            <p className="text-xs text-telegram-secondary">
              {isDemo ? '演示数据' : '实时预测市场数据'}
            </p>
          </div>
        </div>
        <button
          onClick={fetchMarkets}
          className="p-1.5 rounded-lg hover:bg-indigo-500/20 transition-colors"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 text-indigo-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/20">
          <div className="flex items-center space-x-2 text-xs text-yellow-400">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {categories.length > 0 && (
        <div className="px-4 py-2 border-b border-indigo-500/20 overflow-x-auto">
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${
                selectedCategory === null ? 'bg-indigo-500 text-white' : 'bg-telegram-dark text-telegram-secondary hover:bg-indigo-500/20'
              }`}
            >
              全部
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${
                  selectedCategory === category ? 'bg-indigo-500 text-white' : 'bg-telegram-dark text-telegram-secondary hover:bg-indigo-500/20'
                }`}
              >
                {getCategoryName(category)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="divide-y divide-indigo-500/10">
        {filteredMarkets.slice(0, 5).map((market) => {
          const prices = parseOutcomePrices(market.outcomePrices)
          return (
            <a
              key={market.id}
              href={`https://polymarket.com/market/${market.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-4 py-3 hover:bg-indigo-500/10 transition-colors group"
            >
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {market.image ? (
                    <img src={market.image} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
                  ) : (
                    <TrendingUp className="w-6 h-6 text-indigo-400/50" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs ${getCategoryColor(market.category)}`}>
                      {getCategoryName(market.category)}
                    </span>
                    <span className="text-xs text-telegram-secondary/70">{formatEndDate(market.endDate)}</span>
                  </div>
                  <h4 className="text-sm font-medium text-telegram-text leading-snug mb-2 group-hover:text-indigo-400 transition-colors line-clamp-2">
                    {market.question}
                  </h4>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 mr-4">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-green-400 font-medium">是 {prices.yes}¢</span>
                        <span className="text-red-400 font-medium">否 {prices.no}¢</span>
                      </div>
                      <div className="h-2 bg-telegram-dark rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: '100%', background: `linear-gradient(to right, #22c55e ${prices.yes}%, #ef4444 ${prices.yes}%)` }} />
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-center text-xs text-telegram-secondary">
                        <Activity className="w-3 h-3 mr-1" />
                        {formatVolume(market.volumeNum)}
                      </div>
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-telegram-secondary/50 flex-shrink-0 group-hover:text-indigo-400 transition-colors" />
              </div>
            </a>
          )
        })}
      </div>

      <div className="px-4 py-3 border-t border-indigo-500/20 bg-indigo-500/5">
        <a href="https://polymarket.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors">
          <span>在 Polymarket 查看更多</span>
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  )
}
